import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { RotateCw, RotateCcw, Maximize, Minimize, ZoomIn, Loader2 } from "lucide-react";
import { getCroppedBlob, readFileAsDataURL } from "@/lib/cropImage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

const PHONE = 9 / 19.2;

const ASPECTS = [
  { key: "phone", label: "Phone", value: PHONE },
  { key: "original", label: "Original", value: null },
  { key: "square", label: "Square", value: 1 },
  { key: "portrait", label: "Portrait", value: 3 / 4 },
  { key: "landscape", label: "Landscape", value: 16 / 9 },
  { key: "share", label: "Share 1.91:1", value: 1200 / 630 },
];

const BG_SWATCHES = ["#000000", "#0A0A0A", "#FFFFFF", "#FAFAFC", "#0055FF", "#E5EFFF", "#34C759"];

export default function ImageEditor({ open, file, slot, defaultAspect = "phone", defaultFit = "cover", onConfirm, onCancel }) {
  const [src, setSrc] = useState(null);
  const [origAspect, setOrigAspect] = useState(null);
  const [aspectKey, setAspectKey] = useState(defaultAspect);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixels, setPixels] = useState(null);
  const [fit, setFit] = useState(defaultFit);
  const [bg, setBg] = useState(slot === "logo" ? "#FAFAFC" : "#0A0A0A");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) return;
    setAspectKey(defaultAspect); setFit(defaultFit);
    setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0);
    readFileAsDataURL(file).then((url) => {
      setSrc(url);
      const img = new Image();
      img.onload = () => setOrigAspect(img.width / img.height);
      img.src = url;
    });
  }, [file, defaultAspect, defaultFit]);

  const onComplete = useCallback((_, px) => setPixels(px), []);

  const aspectValue = (() => {
    const a = ASPECTS.find((x) => x.key === aspectKey);
    if (a?.value != null) return a.value;
    return origAspect || 1;
  })();

  const confirm = async () => {
    if (!pixels) return;
    setSaving(true);
    const mime = slot === "logo" ? "image/png" : "image/jpeg";
    const blob = await getCroppedBlob(src, pixels, rotation, { mime, bg });
    const ext = mime === "image/png" ? "png" : "jpg";
    const out = new File([blob], `${slot}.${ext}`, { type: mime });
    await onConfirm({ file: out, fit, bg });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-3xl" data-testid="image-editor">
        <DialogHeader>
          <DialogTitle>Edit image</DialogTitle>
          <DialogDescription>Crop, zoom and rotate your image, then choose how it fits the frame.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
          {/* crop area */}
          <div className="relative h-[360px] w-full overflow-hidden rounded-xl bg-neutral-900" style={{ background: fit === "contain" ? bg : "#171717" }}>
            {src && (
              <Cropper
                image={src}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspectValue}
                restrictPosition={true}
                minZoom={1}
                maxZoom={4}
                objectFit="contain"
                showGrid={true}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onComplete}
              />
            )}
          </div>

          {/* controls */}
          <div className="space-y-5">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">Crop shape</p>
              <div className="flex flex-wrap gap-1.5">
                {ASPECTS.map((a) => (
                  <button key={a.key} type="button" onClick={() => setAspectKey(a.key)}
                    className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${aspectKey === a.key ? "border-brand bg-brand text-white" : "border-black/10 text-neutral-600 hover:border-brand/40"}`}
                    data-testid={`editor-aspect-${a.key}`}>{a.label}</button>
                ))}
              </div>
              <p className="mt-1.5 text-[11px] text-neutral-400">Pick “Original” to keep the whole image, then use “Fit” below so nothing gets cut.</p>
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-500"><ZoomIn className="h-3.5 w-3.5" /> Zoom</div>
              <Slider value={[zoom]} min={1} max={4} step={0.01} onValueChange={(v) => setZoom(v[0])} data-testid="editor-zoom" />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-neutral-500">
                <span>Rotate</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setRotation((r) => (r - 90 + 360) % 360)} className="rounded-md border border-black/10 p-1 hover:bg-black/5" data-testid="editor-rotate-left"><RotateCcw className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => setRotation((r) => (r + 90) % 360)} className="rounded-md border border-black/10 p-1 hover:bg-black/5" data-testid="editor-rotate-right"><RotateCw className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <Slider value={[rotation]} min={0} max={360} step={1} onValueChange={(v) => setRotation(v[0])} data-testid="editor-rotation" />
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">Display in frame</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setFit("cover")} className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-semibold transition-colors ${fit === "cover" ? "border-brand bg-brand text-white" : "border-black/10 text-neutral-600"}`} data-testid="editor-fit-cover"><Maximize className="h-3.5 w-3.5" /> Fill</button>
                <button type="button" onClick={() => setFit("contain")} className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-semibold transition-colors ${fit === "contain" ? "border-brand bg-brand text-white" : "border-black/10 text-neutral-600"}`} data-testid="editor-fit-contain"><Minimize className="h-3.5 w-3.5" /> Fit (no crop)</button>
              </div>
              <p className="mt-1.5 text-[11px] text-neutral-400">{fit === "cover" ? "Fills the frame — edges may be cropped." : "Shows the entire image with a background — nothing is cut."}</p>
            </div>

            {fit === "contain" && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">Background</p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {BG_SWATCHES.map((c) => (
                    <button key={c} type="button" onClick={() => setBg(c)} style={{ background: c }}
                      className={`h-7 w-7 rounded-full ring-2 ${bg === c ? "ring-brand" : "ring-black/10"}`} data-testid={`editor-bg-${c.slice(1)}`} />
                  ))}
                  <input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="h-7 w-9 cursor-pointer rounded border border-black/10 bg-transparent" data-testid="editor-bg-custom" />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="editor-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={saving || !pixels} className="bg-brand hover:bg-brand-dark" data-testid="editor-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
